import {
  buildHeight,
  hipHeight,
  legLength,
  ownMass,
  segmentLength,
  type HumanoidModel,
  type SimState,
} from '@/domain/hs1'

/**
 * Die Zahlen, die der Fachtext aus dem Modell holt statt sie zu behaupten.
 *
 * DAS PROBLEM. In den Kapiteln standen Zahlen im Fliesstext: „Bauhöhe 1,78 m,
 * Masse 80 kg, 31 aktive Freiheitsgrade". Sie waren richtig, als sie
 * geschrieben wurden. Wer danach eine Knieachse verschob oder eine Masse
 * änderte, bekam auf jeder Seite des Simulators die neue Zahl — und im
 * Fachtext daneben weiter die alte. Zwei Wahrheiten über denselben Roboter,
 * und die gedruckt aussehende gewinnt beim Lesen.
 *
 * DIE LÖSUNG IST BEWUSST KLEIN. Kein Templatemotor, keine Ausdrücke im Text.
 * Der Text schreibt `{{koerpermasse}}`, diese Datei sagt, was das ist, und
 * `ReferenceBody` setzt es vor dem Einfügen ein. Mehr würde bedeuten, dass im
 * Fachtext gerechnet wird — und dann stünde die Rechnung an zwei Orten.
 *
 * WAS EIN UNBEKANNTER SCHLÜSSEL TUT: er bleibt stehen und wird sichtbar
 * markiert. Ein Platzhalter, der still verschwindet, hinterlässt einen Satz
 * mit einem Loch, das niemand bemerkt.
 */

/** Deutsche Schreibweise: Komma als Dezimaltrennzeichen. */
const zahl = (wert: number, stellen = 1): string =>
  Number.isFinite(wert) ? wert.toFixed(stellen).replace('.', ',') : '—'

export interface ReferenceContext {
  model: HumanoidModel
  state: SimState
  /** Aktive Winkelfreiheiten — vom Aufrufer gezählt, nicht hier. */
  activeDofs: number
}

/**
 * Baut die Werteliste zum aktuellen Modell.
 *
 * Die Schlüssel sind deutsch und beschreibend, weil sie im Fliesstext stehen:
 * `{{koerpermasse}}` liest sich im Rohtext noch als Satz, `{{m}}` nicht.
 */
export function referenceValues({ model, state, activeDofs }: ReferenceContext): Record<string, string> {
  const masse = ownMass(model)
  const m = model.masses
  const bein = m.thigh.kg + m.shank.kg + m.foot.kg
  const arm = m.upperArm.kg + m.forearm.kg

  return {
    /* Aufbau und Grundlagen — die Eckdaten des Modellroboters. */
    bauhoehe: zahl(buildHeight(model), 2),
    koerpermasse: zahl(masse, 0),
    /* Die EINGESTELLTE Masse, nicht die aufsummierte. Beide stehen im
       Simulator nebeneinander, und der Unterschied ist eine eigene Aussage —
       siehe die Kennzahl „Gegen die Eingabe" auf der Stückliste. */
    eingestelltemasse: zahl(state.body, 0),
    freiheitsgrade: String(activeDofs),
    hueftachse: zahl(hipHeight(model), 2),
    beinlaenge: zahl(legLength(model), 2),
    oberschenkel: zahl(segmentLength(model, 'thigh'), 2),
    unterschenkel: zahl(segmentLength(model, 'shank'), 2),
    schulterbreite: zahl(Math.abs(model.points.shoulder.y * 2) / 1000, 2),

    /*
      Massenverteilung — die Beschriftungen der Aufbauzeichnung.

      SIE STEHEN IM SVG, und das geht, weil hier auf dem ROHTEXT ersetzt wird
      und nicht im DOM: eine Zeichnung ist an dieser Stelle nur Text mit
      spitzen Klammern.
    */
    rumpfmasse: zahl(m.trunk.kg, 1),
    rumpfanteil: zahl((m.trunk.kg / masse) * 100, 1),
    kopfmasse: zahl(m.head.kg, 1),
    beinmasse: zahl(bein, 1),
    armmasse: zahl(arm, 1),
    oberarm: zahl(segmentLength(model, 'upperArm'), 2),
    oberarmmasse: zahl(m.upperArm.kg, 1),
    unterarm: zahl(segmentLength(model, 'forearm'), 2),
    unterarmmasse: zahl(m.forearm.kg, 1),
    oberschenkelmasse: zahl(m.thigh.kg, 1),
    unterschenkelmasse: zahl(m.shank.kg, 1),
    fussmasse: zahl(m.foot.kg, 1),
  }
}

/** Was ein nicht aufgelöster Platzhalter im Text hinterlässt. */
const FEHLT = (key: string) =>
  `<b class="ref-missing" title="Kein Wert für {{${key}}}">{{${key}}}</b>`

/**
 * Setzt die Werte in den Rohtext ein.
 *
 * Läuft VOR dem Einfügen ins Dokument, auf dem String — nicht danach auf dem
 * DOM. Ein Platzhalter, der kurz sichtbar wird und dann ersetzt, ist ein
 * Flackern; und ein Text, der erst im DOM zusammengesetzt wird, lässt sich
 * nicht mehr am Stück prüfen.
 */
export function fillReference(html: string, values: Record<string, string>): string {
  return html.replace(/\{\{([a-z]+)\}\}/g, (_, key: string) =>
    values[key] !== undefined ? values[key] : FEHLT(key),
  )
}
